/**
 * Tutorial Settings Tab
 *
 * Shows the main onboarding tutorial (Welcome Tour + Demo Document).
 */
import { Button, notify } from '@affine/component';
import {
  SettingHeader,
  SettingRow,
  SettingWrapper,
} from '@affine/component/setting-components';
import { useSystemOnline } from '@affine/core/components/hooks/use-system-online';
import { GraphQLService } from '@affine/core/modules/cloud';
import { WorkspaceDialogService } from '@affine/core/modules/dialogs';
import { DocsService } from '@affine/core/modules/doc';
import { AccessibleFolderCacheStore } from '@affine/core/modules/organize';
// sl-u4d2: DemoDocService removed — onboarding is backend-seeded
import { useStartWelcomeTour } from '@affine/core/modules/streamlain/tutorial';
import { WorkbenchService } from '@affine/core/modules/workbench';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { reimportOnboardingDocMutation } from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { BRAND_DISPLAY_NAME } from '@streamlain/brand';
import { useLiveData, useService } from '@toeverything/infra';
import { useCallback, useMemo, useState } from 'react';

function reimportDemoErrorMessage(
  t: ReturnType<typeof useI18n>,
  error: unknown
) {
  const message = error instanceof Error ? error.message : String(error);
  if (/permission denied/i.test(message)) {
    return (
      t[
        'com.streamlain.settings.workspace.onboarding.reimport-permission-denied'
      ]?.() ??
      'You need permission to create documents in this workspace before you can import the demo document.'
    );
  }
  return message;
}

export const TutorialsSettings = () => {
  const t = useI18n();

  return (
    <>
      <SettingHeader
        title={t['com.streamlain.settings.tutorials.title']?.() ?? 'Tutorial'}
        subtitle={
          t['com.streamlain.settings.tutorials.subtitle']?.() ??
          `Interactive guides to help you get the most out of ${BRAND_DISPLAY_NAME}.`
        }
      />
      <OnboardingSection />
    </>
  );
};

// --- Onboarding Section ---

const OnboardingSection = () => {
  const t = useI18n();
  const handleStartTour = useStartWelcomeTour();
  const dialogService = useService(WorkspaceDialogService);
  const workbench = useService(WorkbenchService).workbench;
  const graphqlService = useService(GraphQLService);
  const workspace = useService(WorkspaceService).workspace;
  const [isReimporting, setIsReimporting] = useState(false);
  const isOnline = useSystemOnline();

  const docsService = useService(DocsService);
  const accessibleFolderCache = useService(AccessibleFolderCacheStore);
  const demoDocs = useLiveData(docsService.list.demoDocs$);

  const demoDoc = useMemo(() => {
    return demoDocs.find(doc => !doc.trash$.value) ?? demoDocs[0] ?? null;
  }, [demoDocs]);

  const demoDocStatus = useMemo(() => {
    if (!demoDoc) return 'absent' as const;
    return demoDoc.trash$.value ? ('trashed' as const) : ('present' as const);
  }, [demoDoc]);
  const hasUsableDemoDoc = demoDocStatus === 'present';

  const handleOpenDemo = useCallback(() => {
    if (demoDoc) {
      dialogService.closeAll();
      workbench.openDoc(demoDoc.id);
    }
  }, [demoDoc, dialogService, workbench]);

  const handleReimportDemo = useCallback(async () => {
    if (isReimporting) return;
    if (!isOnline) {
      notify.error({
        title:
          t[
            'com.streamlain.settings.workspace.onboarding.reimport-offline'
          ]?.() ?? 'Demo document can only be imported while online.',
      });
      return;
    }
    setIsReimporting(true);
    try {
      const result = await graphqlService.gql({
        query: reimportOnboardingDocMutation,
        variables: {
          workspaceId: workspace.id,
          locale: t.language,
        },
      });
      const docId = result.reimportOnboardingDoc;
      await Promise.all([
        docsService.ensureDocCached(docId),
        accessibleFolderCache.refreshRootFolderDocIds(),
      ]);
      notify.success({
        title:
          t[
            'com.streamlain.settings.workspace.onboarding.reimport-success'
          ]?.() ?? 'Demo document imported successfully.',
      });
      dialogService.closeAll();
      workbench.openDoc({ docId, mode: 'edgeless' as const });
    } catch (error) {
      notify.error({
        title:
          t[
            'com.streamlain.settings.workspace.onboarding.reimport-error'
          ]?.() ?? 'Failed to import demo document.',
        message: reimportDemoErrorMessage(t, error),
      });
    } finally {
      setIsReimporting(false);
    }
  }, [
    dialogService,
    accessibleFolderCache,
    docsService,
    graphqlService,
    isOnline,
    isReimporting,
    t,
    workbench,
    workspace.id,
  ]);

  const demoDocDesc =
    demoDocStatus === 'present'
      ? (t['com.streamlain.settings.tutorials.onboarding.demo-available']?.() ??
        `Open the demo document to explore ${BRAND_DISPLAY_NAME} features.`)
      : demoDocStatus === 'trashed'
        ? (t[
            'com.streamlain.settings.workspace.onboarding.status-trashed'
          ]?.() ?? 'Demo document is in trash')
        : (t[
            'com.streamlain.settings.workspace.onboarding.status-absent'
          ]?.() ?? 'Demo document not found');

  return (
    <SettingWrapper
      title={
        t['com.streamlain.settings.tutorials.onboarding.title']?.() ??
        'Onboarding'
      }
    >
      <SettingRow
        name={
          t['com.streamlain.settings.tutorials.onboarding.welcome']?.() ??
          'Welcome Tour'
        }
        desc={
          t['com.streamlain.settings.tutorials.onboarding.welcome-desc']?.() ??
          `A quick introduction to the ${BRAND_DISPLAY_NAME} interface.`
        }
        data-testid="tutorials-onboarding-welcome"
      >
        <Button
          variant="secondary"
          onClick={handleStartTour}
          disabled={!hasUsableDemoDoc}
          data-testid="start-welcome-tour-button"
        >
          {t['com.streamlain.settings.tutorials.start-tour']?.() ??
            'Start Tour'}
        </Button>
      </SettingRow>
      <SettingRow
        name={
          t['com.streamlain.settings.workspace.onboarding.demo-doc']?.() ??
          'Demo Document'
        }
        desc={demoDocDesc}
        data-testid="tutorials-demo-doc"
      >
        {demoDocStatus === 'present' ? (
          <Button
            variant="secondary"
            onClick={handleOpenDemo}
            data-testid="open-demo-doc-button"
          >
            {t['com.streamlain.settings.tutorials.open']?.() ?? 'Open'}
          </Button>
        ) : (
          <Button
            variant="secondary"
            onClick={() => {
              void handleReimportDemo();
            }}
            disabled={!isOnline}
            loading={isReimporting}
            data-testid="reimport-demo-doc-button"
          >
            {isReimporting
              ? (t[
                  'com.streamlain.settings.workspace.onboarding.reimporting'
                ]?.() ?? 'Importing...')
              : (t[
                  'com.streamlain.settings.workspace.onboarding.reimport-button'
                ]?.() ?? 'Re-import Demo Document')}
          </Button>
        )}
      </SettingRow>
    </SettingWrapper>
  );
};
